import { createSlice } from '@reduxjs/toolkit';
import { AppThunk } from './store';
import { apiString as API_URL } from '../utils/constants';
import axios from 'axios';
import { setIsLoading, setShowMessage } from './shared.slice';
import { Role } from '../Models/Role/Role.model';
import { copyObject } from '../utils/common';

const adminRoles = createSlice({
    name: 'adminRoles',
    initialState: {
        assignedAssessments: [],
        assignedWidgets: [],
        assessmentsByRole: [],
        openNew: false,
        roles: [],
        selectAllAssessments: false,
        selectAllPermissions: false,
        selectAllWidgets: false,
        selectAllFacilities: false,
        selectedRole: new Role(),
        roleName: '',
        rolePermissions: [],
        smartdashboardWidgets: [],
        analyticsWidgets: [],
        hedisMetricsWidgets: [],
        assignedFacilities: [],
        roleFacilities: [],
        zipCodes: [],
        selectedTab: 0,
        providerRole: new Role(),
    },
    reducers: {
        _setAssessmentsByRole(state, action) {
            state.assessmentsByRole = action.payload;
        },
        _setAssignedWidgets(state, action) {
            state.assignedWidgets = action.payload;
        },
        _setOpenNew(state, action) {
            state.openNew = action.payload;
        },
        _setRoles(state, action) {
            state.roles = action.payload;
        },
        _setSelectAllAssessments(state, action) {
            state.selectAllAssessments = action.payload;
        },
        _setSelectAllPermissions(state, action) {
            state.selectAllPermissions = action.payload;
        },
        _setSelectAllWidgets(state, action) {
            state.selectAllWidgets = action.payload;
        },
        _setSelectAllFacilities(state, action) {
            state.selectAllFacilities = action.payload;
        },
        _setSelectedRole(state, action) {
            state.selectedRole = action.payload;
        },
        _setRoleName(state, action) {
            state.roleName = action.payload;
        },
        _setAssignedAssessments(state, action) {
            state.assignedAssessments = action.payload;
        },
        _setRolePermissions(state, action) {
            state.rolePermissions = action.payload;
        },
        _setSmartdashboardWidgets(state, action) {
            state.smartdashboardWidgets = action.payload;
        },
        _setAnalyticsWidgets(state, action) {
            state.analyticsWidgets = action.payload;
        },
        _setHedisMetricsWidgets(state, action) {
            state.hedisMetricsWidgets = action.payload;
        },
        _setRoleFacilities(state, action) {
            state.roleFacilities = action.payload;
        },
        _setAssignedFacilities(state, action) {
            state.assignedFacilities = action.payload;
        },
        _setZipCodes(state, action) {
            state.zipCodes = action.payload;
        },
        _setSelectedTab(state, action) {
            state.selectedTab = action.payload;
        },
        _setProviderRole(state, action) {
            state.providerRole = action.payload;
        },
    },
});

const {
    _setAssessmentsByRole,
    _setAssignedWidgets,
    _setOpenNew,
    _setRoles,
    _setSelectAllAssessments,
    _setSelectAllPermissions,
    _setSelectAllWidgets,
    _setSelectAllFacilities,
    _setSelectedRole,
    _setRoleName,
    _setAssignedAssessments,
    _setRolePermissions,
    _setSmartdashboardWidgets,
    _setAnalyticsWidgets,
    _setHedisMetricsWidgets,
    _setRoleFacilities,
    _setAssignedFacilities,
    _setZipCodes,
    _setSelectedTab,
    _setProviderRole,
} = adminRoles.actions;

export const setAssessmentsByRole =
    (value: any[]): AppThunk =>
    (dispatch) => {
        dispatch(_setAssessmentsByRole(value));
    };

export const setAssignedWidgets =
    (value: any[]): AppThunk =>
    (dispatch) => {
        dispatch(_setAssignedWidgets(value));
    };

export const setOpenNew =
    (value: boolean): AppThunk =>
    (dispatch) => {
        dispatch(_setOpenNew(value));
    };

export const setSelectAllAssessments =
    (value: boolean): AppThunk =>
    (dispatch) => {
        dispatch(_setSelectAllAssessments(value));
    };

export const setSelectAllPermissions =
    (value: boolean): AppThunk =>
    (dispatch) => {
        dispatch(_setSelectAllPermissions(value));
    };

export const setSelectAllWidgets =
    (value: boolean): AppThunk =>
    (dispatch) => {
        dispatch(_setSelectAllWidgets(value));
    };

export const setSelectAllFacilities =
    (value: boolean): AppThunk =>
    (dispatch) => {
        dispatch(_setSelectAllFacilities(value));
    };

export const setRoles =
    (value: any[]): AppThunk =>
    (dispatch) => {
        dispatch(_setRoles(value));
    };

export const setSelectedRole =
    (value: Role): AppThunk =>
    (dispatch) => {
        dispatch(_setSelectedRole(value));
    };

export const setRoleName =
    (value: string): AppThunk =>
    (dispatch) => {
        dispatch(_setRoleName(value));
    };

export const setAssignedAssessments =
    (value: any[]): AppThunk =>
    (dispatch) => {
        dispatch(_setAssignedAssessments(value));
    };

export const setRolePermissions =
    (value: any[]): AppThunk =>
    (dispatch) => {
        dispatch(_setRolePermissions(value));
    };

export const setRoleFacilities =
    (value: any[]): AppThunk =>
    (dispatch) => {
        dispatch(_setRoleFacilities(value));
    };

export const setAssignedFacilities =
    (value: any[]): AppThunk =>
    (dispatch) => {
        dispatch(_setAssignedFacilities(value));
    };

export const setZipCodes =
    (value: any[]): AppThunk =>
    (dispatch) => {
        dispatch(_setZipCodes(value));
    };

export const setSelectedTab =
    (value: any): AppThunk =>
    (dispatch) => {
        dispatch(_setSelectedTab(value));
    };

export const getInitialValues = (): AppThunk => (dispatch) => {
    dispatch(setRolePermissions([]));
    dispatch(setAssignedWidgets([]));
    dispatch(setAssignedFacilities([]));
    dispatch(setAssignedAssessments([]));
    dispatch(setRoleName(''));
    dispatch(setSelectedRole(new Role()));
    dispatch(setSelectedTab(0));
};

export const updateRolesList =
    (data: Role): AppThunk =>
    (dispatch, getState) => {
        const { roles } = getState().adminRoles;
        const rolesCopy = copyObject(roles);
        const index = rolesCopy.findIndex((role) => role.Id === data.Id);
        if (index !== -1) {
            rolesCopy[index] = data;
        } else {
            rolesCopy.push(data);
        }
        dispatch(setRoles(rolesCopy));
    };

export const closeEditHandler = (): AppThunk => (dispatch) => {
    dispatch(setOpenNew(false));
    dispatch(getInitialValues());
};

export const openNewHandler = (): AppThunk => (dispatch) => {
    dispatch(setOpenNew(true));
    dispatch(getInitialValues());
};

export const fetchRoles = (): AppThunk => async (dispatch) => {
    dispatch(setIsLoading(true));
    const response = await axios.get(`${API_URL}/role/roles`);
    dispatch(setRoles(response.data));
    dispatch(setIsLoading(false));
};

export const fetchSmartDashboardWidgets = (): AppThunk => async (dispatch) => {
    dispatch(setIsLoading(true));
    const response = await axios.get(`${API_URL}/role/smartdashboardwidgets`);
    dispatch(_setSmartdashboardWidgets(response.data));
    dispatch(setIsLoading(false));
};

export const fetchAnalyticsWidgets = (): AppThunk => async (dispatch) => {
    dispatch(setIsLoading(true));
    const response = await axios.get(`${API_URL}/role/analyticswidgets`);
    dispatch(_setAnalyticsWidgets(response.data));
    dispatch(setIsLoading(false));
};

export const fetchHedisMetricsWidgets = (): AppThunk => async (dispatch) => {
    dispatch(setIsLoading(true));
    const response = await axios.get(`${API_URL}/role/hedismetricswidgets`);
    dispatch(_setHedisMetricsWidgets(response.data));
    dispatch(setIsLoading(false));
};

export const fetchFacilities = (): AppThunk => async (dispatch) => {
    dispatch(setIsLoading(true));
    const response = await axios.get(`${API_URL}/facility/getactivefacilities`);
    dispatch(_setRoleFacilities(response.data));
    dispatch(setIsLoading(false));
};

export const fetchFacilityZipCoeds = (): AppThunk => async (dispatch) => {
    dispatch(setIsLoading(true));
    const response = await axios.get(`${API_URL}/facility/getfacilityzipcodes`);
    dispatch(_setZipCodes(response.data));
    dispatch(setIsLoading(false));
};

export const fetchAssessmentsByRole = (): AppThunk => async (dispatch) => {
    dispatch(setIsLoading(true));
    const response = await axios.get(`${API_URL}/role/assessmentsbyrole`);
    dispatch(setAssessmentsByRole(response.data));
    dispatch(setIsLoading(false));
};

export const handleSaveRole = (): AppThunk => async (dispatch, getState) => {
    const { openNew, selectedRole, roleName, rolePermissions, assignedWidgets, assignedFacilities, assignedAssessments } = getState().adminRoles;
    const roleCopy = structuredClone(selectedRole);
    roleCopy.Name = roleName;
    roleCopy.Permissions = rolePermissions;
    roleCopy.Widgets = assignedWidgets;
    roleCopy.ProviderFacilities = assignedFacilities;
    roleCopy.AssignedAssessments = assignedAssessments;
    openNew ? dispatch(insertRole(roleCopy)) : dispatch(updateRole(roleCopy));
};

export const fetchRoleByRoleName =
    (roleName: string): AppThunk =>
    async (dispatch) => {
        const response = await axios.get(`${API_URL}/role/getrolebyrolename?roleName=${roleName}`);
        dispatch(_setProviderRole(response.data));
        return response.data;
    };

export const insertRole =
    (data: {}): AppThunk =>
    async (dispatch) => {
        try {
            dispatch(setIsLoading(true));
            const response = await axios.post(`${API_URL}/role/insert`, data);
            dispatch(updateRolesList(response.data));
            dispatch(closeEditHandler());
            dispatch(fetchRoles());
            dispatch(setIsLoading(false));
            dispatch(setShowMessage(true, 'Success', 'success'));
        } catch (error) {
            console.log(error);
            dispatch(setIsLoading(false));
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
        }
    };

export const updateRole =
    (data: {}): AppThunk =>
    async (dispatch) => {
        try {
            dispatch(setIsLoading(true));
            const response = await axios.put(`${API_URL}/role/update`, data);
            dispatch(updateRolesList(response.data));
            dispatch(closeEditHandler());
            dispatch(setIsLoading(false));
            dispatch(setShowMessage(true, 'Success', 'success'));
        } catch (error) {
            console.log(error);
            dispatch(setIsLoading(false));
            dispatch(setShowMessage(true, 'Error occurred', 'error'));
        }
    };

export default adminRoles.reducer;
